import { Button, Select, Option } from "@material-tailwind/react";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
import { useWorkorderContext } from "../../../context/WorkorderContext";
import AccordionTitleChevron from "../../copy-button/AccordionTitleChevron";
import CopyToClipboardButton from "../../copy-button/CopyToClipboardButton";

interface MachineHeaderProps {
  isEditing: boolean;
  openAccordion: boolean;
  setOpenAccordion: (open: boolean) => void;
  workorderMachine: EasyTech.WorkorderMachine;
  machinesCustomer: EasyTech.Machine[];
  selectedMachineId: string;
  setSelectedMachineId: (id: string) => void;
  canEdit: boolean;
  onEditClick: () => void;
  onSaveClick: () => void;
  onCancelEdit: () => void;
  onDeleteClick: () => void;
  onAddHose?: () => void;
  onAddPart?: () => void;
}

/**
 * Composant d'en-tête pour l'affichage d'une machine dans un workorder
 * Gère l'affichage du titre, des informations de base et des actions (édition, suppression)
 * Modes : normal, édition
 */
export default function MachineHeader({
  isEditing,
  openAccordion,
  setOpenAccordion,
  workorderMachine,
  machinesCustomer,
  selectedMachineId,
  setSelectedMachineId,
  onEditClick,
  onSaveClick,
  onCancelEdit,
  onDeleteClick,
  canEdit,
  onAddHose,
  onAddPart,
}: MachineHeaderProps) {
  // Accéder au contexte pour vérifier les machines existantes
  const { getMachines, editingPartId } = useWorkorderContext();

  // Obtenir les machines existantes dans le workorder
  const existingMachines = getMachines();

  // Fonction pour vérifier si une machine est déjà utilisée dans le workorder
  const isMachineUsed = (machineId: string) => {
    return existingMachines.some(
      (m) => m.id !== workorderMachine.id && m.machine?.id === machineId
    );
  };

  const machineTitle = `${workorderMachine.machine.brand.label} - ${workorderMachine.machine.type.label}`;
  const machineCopyText = workorderMachine.machine.serialNumber
    ? `${machineTitle} ${workorderMachine.machine.serialNumber}`
    : machineTitle;

  return (
    <div className="flex justify-between items-center w-full">
      <div className="flex items-center gap-3">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Select
              value={selectedMachineId}
              onChange={(val) => {
                setSelectedMachineId(val || "");
              }}
              className="min-w-[300px] border border-blue-500 rounded bg-white"
              size="md"
              variant="outlined"
              color="blue"
              label={t("workorder:machines:selectMachine")}
              disabled={!canEdit}
            >
              {machinesCustomer.length > 0 ? (
                machinesCustomer.map((el) => {
                  const isUsed = isMachineUsed(el.id);
                  return (
                    <Option
                      key={el.id}
                      value={el.id}
                      disabled={isUsed}
                      className={`hover:bg-blue-50 ${
                        isUsed ? "opacity-50" : ""
                      }`}
                    >
                      {`${el.brand.label}${
                        el.type ? ` - ${el.type.label}` : ""
                      }${el.serialNumber ? ` - ${el.serialNumber}` : ""}`}
                      {isUsed &&
                        ` (${t(
                          "workorder:machines:errors:machineAlreadyInWorkorder"
                        )})`}
                    </Option>
                  );
                })
              ) : (
                <Option disabled>
                  {t("workorder:machines:errors:noMachine")}
                </Option>
              )}
            </Select>
          </div>
        ) : (
          <div
            onClick={() => setOpenAccordion(!openAccordion)}
            className="cursor-pointer flex items-center gap-2"
          >
            <AccordionTitleChevron open={openAccordion} />
            <h2 className="text-xl font-medium text-gray-900">{machineTitle}</h2>
            {workorderMachine.machine.serialNumber && (
              <p className="text-sm font-medium text-gray-500">
                {workorderMachine.machine.serialNumber}
              </p>
            )}
            <CopyToClipboardButton text={machineCopyText} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {!isEditing ? (
          <>
            <Button
              variant="filled"
              color="white"
              className="normal-case font-thin border border-gray-300"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick();
              }}
              disabled={!canEdit}
            >
              {t("workorder:machines:buttons:deleteMachine")}
            </Button>
            <Button
              variant="filled"
              className="bg-blue-500 normal-case font-thin"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick();
              }}
              disabled={!canEdit}
            >
              {t("workorder:machines:buttons:editMachine")}
            </Button>

            {onAddHose && (
              <Button
                variant="filled"
                size="md"
                className="flex items-center gap-2 bg-blue-50 shadow-md text-sm text-blue-500 px-3 rounded-lg font-thin normal-case"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddHose();
                }}
                disabled={!canEdit || !!editingPartId}
              >
                <PlusCircleIcon className="h-4 w-4" />
                {t("workorder:addHose")}
              </Button>
            )}

            {onAddPart && (
              <Button
                variant="filled"
                size="md"
                className="flex items-center gap-2 bg-blue-50 shadow-md text-sm text-blue-500 px-3 rounded-lg font-thin normal-case"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddPart();
                }}
                disabled={!canEdit || !!editingPartId}
              >
                <PlusCircleIcon className="h-4 w-4" />
                {t("workorder:addPart")}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="filled"
              color="white"
              className="normal-case font-thin border border-gray-300"
              onClick={(e) => {
                e.stopPropagation();
                onCancelEdit();
              }}
              disabled={!canEdit}
            >
              {t("workorder:machines:buttons:cancel")}
            </Button>
            <Button
              variant="filled"
              className="bg-blue-500 normal-case font-thin"
              onClick={(e) => {
                e.stopPropagation();
                onSaveClick();
              }}
              disabled={!canEdit}
            >
              {t("workorder:machines:buttons:save")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
